"use client";

import FlotsIcon from "@/public/FlotsIcon.svg";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocale } from "@/app/providers/IntlProvider";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import { FaUser, FaBuilding } from "react-icons/fa6";
import { useAppDispatch } from "@/app/store/hooks";
import { reopen } from "@/app/store/cookieConsentSlice";
import { navigateToProfileSection } from "./profileNav";
import { homePath } from "@/lib/locale-routing";
import ThemeToggle from "./ThemeToggle";

type FooterFeatureItem = {
  titleKey: string;
  profile: "Individual" | "Organization";
  scrollTo: string;
};

const INDIV_ITEMS: FooterFeatureItem[] = [
  { titleKey: "nav.story.tasks.title", profile: "Individual", scrollTo: "story-build" },
  { titleKey: "nav.story.notes.title", profile: "Individual", scrollTo: "story-action" },
  { titleKey: "nav.story.planday.title", profile: "Individual", scrollTo: "profile-planday" },
];

const ORG_ITEMS: FooterFeatureItem[] = [
  { titleKey: "nav.story.privacy.title", profile: "Organization", scrollTo: "profile-privacy" },
  { titleKey: "nav.story.delegate.title", profile: "Organization", scrollTo: "profile-delegate" },
  { titleKey: "nav.story.live.title", profile: "Organization", scrollTo: "profile-live" },
];

/**
 * Column title. A real heading (h2) so screen-reader users can jump between the footer's
 * groups; `overflow-wrap:anywhere` keeps long uppercase words inside their column when the
 * text is enlarged (WCAG 1.4.4).
 */
function FooterHeading({ id }: { id: string }) {
  return (
    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-main mb-3 [overflow-wrap:anywhere]">
      <FormattedMessage id={id} />
    </h2>
  );
}

/**
 * Footer text link: full-strength colour (the 80 % tint fell under 4.5:1) and 4 px of
 * vertical padding so each target is at least 24 px tall (WCAG 2.5.8); 44 px on phones and
 * tablets, where they are thumb targets.
 */
const FOOTER_LINK =
  "relative group w-fit py-1 max-lg:py-3 text-sm text-sec-blue hover:text-title-blue transition-colors duration-200";

function FooterLink({ href, onClick, children, italic }: {
  href: string;
  onClick?: () => void;
  children: React.ReactNode;
  italic?: boolean;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`${FOOTER_LINK} inline-block ${italic ? "italic" : ""}`}
    >
      {children}
      <span aria-hidden="true" className="absolute left-0 bottom-1 w-0 h-px bg-sec-blue transition-all duration-300 group-hover:w-full" />
    </Link>
  );
}

export default function Footer() {
  const { locale } = useLocale();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const currentRoute = usePathname();
  const router = useRouter();
  const androidLink = useAppLink("footer", { target: "android" });
  const iosLink = useAppLink("footer", { target: "ios" });
  const webLink = useAppLink("footer", { target: "web", webBase: "https://dashboard.flots.app/home" });

  const dispatchProfileNav = (profile: "Individual" | "Organization", scrollTo: string) =>
    navigateToProfileSection(currentRoute, router, profile, scrollTo, locale);

  return (
    <footer className="w-full py-12 px-6 sm:px-8 lg:px-16 xl:px-24 bg-surface border-t border-gray-100 2xl:py-16">
      <div className="max-w-6xl mx-auto 2xl:max-w-7xl">
        {/* Main grid: two link columns on phones too (the brand spans both), so the footer
            is not a single 1 000 px column. */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:gap-10 lg:grid-cols-4 [&>*]:min-w-0">

          {/* Col 1 — Brand */}
          <div className="col-span-2 flex flex-col gap-4 sm:col-span-1">
            <div className="flex items-center gap-2 text-sec-blue">
              <FlotsIcon className="logo-mark w-8 h-8" aria-hidden="true" />
              <p className="text-xl font-bold italic">flots</p>
            </div>

            <p className="text-xs text-gray-main leading-relaxed">
              <FormattedMessage id="footer.copyright" />
            </p>

            <ThemeToggle />

            <div className="flex items-center gap-3 mt-1">
              <Link
                href={androidLink.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("footer-android-button", {}, { criticalOnNav: true });
                  trackCtaClick("footer", "android");
                }}
                className="w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-blue-main hover:bg-blue-fill transition-colors"
              >
                <Image
                  src="/hero/PlayStoreIcon.png"
                  alt={intl.formatMessage({ id: "a11y.downloadGooglePlay" })}
                  width={22}
                  height={22}
                  className="h-[22px] w-[22px]"
                />
              </Link>
              <Link
                href={iosLink.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("footer-apple-button", {}, { criticalOnNav: true });
                  trackCtaClick("footer", "ios");
                }}
                className="w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-blue-main hover:bg-blue-fill transition-colors"
              >
                <Image
                  src="/hero/AppleIcon.png"
                  alt={intl.formatMessage({ id: "a11y.downloadAppStore" })}
                  width={20}
                  height={20}
                  className="h-5 w-5"
                />
              </Link>
              <Link
                href={webLink.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("footer-web-button", {}, { criticalOnNav: true });
                  trackCtaClick("footer", "web");
                }}
                className="w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-blue-main hover:bg-blue-fill transition-colors"
              >
                <Image
                  src="/hero/WebIcon.png"
                  alt={intl.formatMessage({ id: "a11y.openWebApp" })}
                  width={22}
                  height={22}
                  className="h-[22px] w-[22px]"
                />
              </Link>
            </div>
          </div>

          {/* Col 2 — Features */}
          <div className="flex flex-col sm:col-span-1 lg:col-span-1">
            <FooterHeading id="footer.features.title" />
            <div className="flex flex-col gap-4">
              {/* Individuals */}
              <div>
                <h3 className="flex items-center gap-1.5 mb-1">
                  <FaUser aria-hidden="true" className="text-brand-ink text-xs" />
                  <span className="text-xs font-semibold text-brand-ink uppercase tracking-wide">
                    <FormattedMessage id="nav.features.indiv.label" />
                  </span>
                </h3>
                <ul className="flex flex-col gap-0.5">
                  {INDIV_ITEMS.map((item) => (
                    <li key={item.scrollTo}>
                      <button
                        onClick={() => {
                          trackEvent("footer-feature-item", { scrollTo: item.scrollTo });
                          dispatchProfileNav(item.profile, item.scrollTo);
                        }}
                        className={`${FOOTER_LINK} text-left cursor-pointer`}
                      >
                        <FormattedMessage id={item.titleKey} />
                        <span aria-hidden="true" className="absolute left-0 bottom-1 w-0 h-px bg-sec-blue transition-all duration-300 group-hover:w-full" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Organizations */}
              <div>
                <h3 className="flex items-center gap-1.5 mb-1">
                  <FaBuilding aria-hidden="true" className="text-brand-ink text-xs" />
                  <span className="text-xs font-semibold text-brand-ink uppercase tracking-wide">
                    <FormattedMessage id="nav.features.org.label" />
                  </span>
                </h3>
                <ul className="flex flex-col gap-0.5">
                  {ORG_ITEMS.map((item) => (
                    <li key={item.scrollTo}>
                      <button
                        onClick={() => {
                          trackEvent("footer-feature-item", { scrollTo: item.scrollTo });
                          dispatchProfileNav(item.profile, item.scrollTo);
                        }}
                        className={`${FOOTER_LINK} text-left cursor-pointer`}
                      >
                        <FormattedMessage id={item.titleKey} />
                        <span aria-hidden="true" className="absolute left-0 bottom-1 w-0 h-px bg-sec-blue transition-all duration-300 group-hover:w-full" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Col 3 — Navigation */}
          <div className="flex flex-col">
            <FooterHeading id="footer.nav.title" />
            <ul className="flex flex-col gap-0.5">
              <li>
                <FooterLink href={`${homePath(locale)}#hero`} onClick={() => trackEvent("footer-presentation-link")}>
                  <FormattedMessage id="footer.presentation" />
                </FooterLink>
              </li>
              <li>
                <FooterLink href={`${homePath(locale)}#pricing`} onClick={() => trackEvent("footer-pricing-link")}>
                  <FormattedMessage id="footer.pricing" />
                </FooterLink>
              </li>
              <li>
                <FooterLink href={`${homePath(locale)}#contact`} onClick={() => trackEvent("footer-contact-link")}>
                  <FormattedMessage id="footer.contact" />
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  href={locale === "fr" ? "/resources" : "/resources/en"}
                  onClick={() => trackEvent("footer-ressources-link")}
                >
                  <FormattedMessage id="footer.ressources" />
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  href={locale === "fr" ? "/card" : "/card/en"}
                  onClick={() => trackEvent("footer-card-link")}
                >
                  <FormattedMessage id="footer.card" />
                </FooterLink>
              </li>
            </ul>
          </div>

          {/* Col 4 — Legal */}
          <div className="flex flex-col">
            <FooterHeading id="footer.legal.title" />
            <ul className="flex flex-col gap-0.5">
              <li>
                <FooterLink
                  href={locale === "fr" ? "/legal-notice" : "/legal-notice/en"}
                  onClick={() => trackEvent("footer-legal-notice-link")}
                  italic
                >
                  <FormattedMessage id="footer.legalNotice" />
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  href={locale === "fr" ? "/privacy" : "/privacy/en"}
                  onClick={() => trackEvent("footer-privacy-link")}
                  italic
                >
                  <FormattedMessage id="footer.privacy" />
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  href={locale === "fr" ? "/refund" : "/refund/en"}
                  onClick={() => trackEvent("footer-refund-link")}
                  italic
                >
                  <FormattedMessage id="footer.refund" />
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  href={locale === "fr" ? "/terms" : "/terms/en"}
                  onClick={() => trackEvent("footer-terms-link")}
                  italic
                >
                  <FormattedMessage id="footer.terms" />
                </FooterLink>
              </li>
              <li>
                <FooterLink
                  href={locale === "fr" ? "/terms-of-sale" : "/terms-of-sale/en"}
                  onClick={() => trackEvent("footer-cgv-link")}
                  italic
                >
                  <FormattedMessage id="footer.cgv" />
                </FooterLink>
              </li>
              <li>
                <button
                  onClick={() => {
                    trackEvent("footer-manage-cookies");
                    dispatch(reopen());
                  }}
                  className={`${FOOTER_LINK} text-left italic cursor-pointer`}
                >
                  <FormattedMessage id="footer.manageCookies" />
                  <span aria-hidden="true" className="absolute left-0 bottom-1 w-0 h-px bg-sec-blue transition-all duration-300 group-hover:w-full" />
                </button>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </footer>
  );
}
